/**
 * useAddInstance — the server check (/input/validate) behind BOTH ways a row
 * changes: adding one from the Add Instance form, and editing a cell in the
 * instance table.
 *
 *   const { addInstance, validating, revalidateInstance, revalidating } =
 *     useAddInstance({ mode, provider, model });
 *   const row = await addInstance(formRow);            // table row, or null
 *   const next = await revalidateInstance(editedRow);  // updated row, or null
 *
 * By product (`mode`, default: the running product): the request is the same,
 * only the row mapping differs — CCA rows keep quantity / hours as strings,
 * `instance_name` and the server's `adjusted` (components/cca/ccaInstanceRows),
 * and an edited CCA row is re-sent in legacy's own shape (toValidateRow);
 * EIA rows map the EIA way (components/eia/instanceRows).
 *
 * One try / catch per call handles BOTH ways the backend says no — an HTTP
 * error and an HTTP-200 `ErrorCode: -1` envelope (readValidatedRows throws
 * for the latter) — and the toast shows the server's own message when it sent
 * one. Two separate mutation instances so `validating` (an add — the table
 * shows its skeleton) and `revalidating` (an edit — only that row waits) never
 * mix.
 *
 * Every outcome gets a toast, so the user always knows what happened:
 * "Instance added successfully" (legacy's wording, ManageAccount.vue:747),
 * "Instance updated successfully", or the server's reason on failure. Legacy
 * updated a row silently; the user asked for confirmation either way.
 */
import {
  mergeRevalidated as mergeCcaRevalidated,
  toTableRow as toCcaTableRow,
  toValidateRow as toCcaValidateRow,
} from '@components/cca/ccaInstanceRows';
import {
  mergeRevalidated as mergeEiaRevalidated,
  toApiRow,
  toTableRow as toEiaTableRow,
} from '@components/eia/instanceRows';
import { useNotify } from '@hooks/useNotify';
import { APP_MODES, resolveAppMode } from '@variant/appMode';

import {
  VALIDATE_FAILED_MESSAGE,
  readValidatedRows,
  useValidateInstancesMutation,
} from '../api/instancesApi';

export const INSTANCE_ADDED_MESSAGE = 'Instance added successfully.';
export const INSTANCE_UPDATED_MESSAGE = 'Instance updated successfully.';
export const INSTANCE_UPDATE_FAILED_MESSAGE = 'Could not update the instance. Please try again.';

/** The row rules per product. */
const ROW_RULES = Object.freeze({
  [APP_MODES.CCA]: Object.freeze({
    toTableRow: toCcaTableRow,
    toEditRequest: toCcaValidateRow,
    mergeRevalidated: mergeCcaRevalidated,
  }),
  [APP_MODES.EIA]: Object.freeze({
    toTableRow: toEiaTableRow,
    toEditRequest: (row) => toApiRow(row),
    mergeRevalidated: mergeEiaRevalidated,
  }),
});

export function useAddInstance({ mode = resolveAppMode(), provider, model }) {
  const rules = ROW_RULES[mode] ?? ROW_RULES[APP_MODES.EIA];
  const [validateAdd, { isLoading: validating }] = useValidateInstancesMutation();
  const [validateEdit, { isLoading: revalidating }] = useValidateInstancesMutation();
  const notify = useNotify();

  const addInstance = async (row) => {
    try {
      const response = await validateAdd({ provider, model, rows: [row] }).unwrap();
      const [validated] = readValidatedRows(response);
      notify.success(INSTANCE_ADDED_MESSAGE);
      return rules.toTableRow(row, validated);
    } catch (error) {
      notify.error(error, VALIDATE_FAILED_MESSAGE);
      return null;
    }
  };

  const revalidateInstance = async (editedRow) => {
    try {
      const response = await validateEdit({
        provider,
        model,
        rows: [rules.toEditRequest(editedRow, provider)],
      }).unwrap();
      const [validated] = readValidatedRows(response);
      notify.success(INSTANCE_UPDATED_MESSAGE);
      return rules.mergeRevalidated(editedRow, validated);
    } catch (error) {
      notify.error(error, INSTANCE_UPDATE_FAILED_MESSAGE);
      return null;
    }
  };

  return { addInstance, validating, revalidateInstance, revalidating };
}
